<template>
  <nav aria-label="Progress">
    <ol
      role="list"
      class="divide-y divide-gray-300 overflow-auto rounded-md border border-gray-300 md:flex md:divide-y-0 dark:border dark:border-white dark:bg-slate-700"
    >
      <li
        v-for="(step, stepIdx) in steps"
        :key="step.name"
        class="relative md:flex md:flex-1 dark:bg-slate-700"
      >
        <div
          v-if="step.status === 'complete'"
          class="group bg-primary-500 flex w-full items-center"
          :class="{
            'cursor-pointer': !step.locked,
            'cursor-default': step.locked
          }"
          @click="!step.locked && (localStep = step.id)"
        >
          <span class="flex items-center text-sm font-medium" :class="padding">
            <span
              class="flex shrink-0 items-center justify-center rounded-full bg-white dark:bg-slate-700"
              :class="[
                step.action ? 'group-hover:bg-primary-800' : '',
                iconSize
              ]"
            >
              <FontAwesomeIcon
                :icon="['fas', 'check']"
                class="text-primary h-4 w-4"
                aria-hidden="true"
              />
            </span>
            <span
              class="capitalize-first ml-4 truncate font-medium text-white"
              :class="{
                'text-sm': size === 'extra-small'
              }"
            >
              {{ step.name }}
            </span>
          </span>
        </div>
        <div
          v-else-if="step.status === 'current'"
          class="flex items-center text-sm font-medium"
          :class="padding"
          aria-current="step"
        >
          <span
            class="dark:border-primary-500 flex shrink-0 items-center justify-center rounded-full"
            :class="[
              iconSize,
              step.valid
                ? 'border-primary-500 bg-primary border'
                : 'bg-secondary-500 dark:border-primary-500'
            ]"
          >
            <span
              v-if="step.valid"
              class="flex shrink-0 items-center justify-center rounded-full dark:bg-slate-700"
            >
              <FontAwesomeIcon
                :icon="['fas', 'check']"
                class="h-4 w-4 text-white"
                aria-hidden="true"
              />
            </span>
            <FontAwesomeIcon
              v-else
              class="text-contrast-secondary h-2 w-2"
              :icon="['fas', 'circle']"
            />
          </span>
          <span
            class="text-primary-500 capitalize-first ml-4 truncate font-bold"
            :class="{
              'text-sm': size === 'extra-small'
            }"
          >
            {{ step.name }}
          </span>
        </div>
        <div v-else class="group flex items-center">
          <span class="flex items-center text-sm font-medium" :class="padding">
            <span
              class="flex shrink-0 items-center justify-center rounded-full border border-gray-300 dark:border dark:border-white dark:bg-slate-700"
              :class="[
                step.action
                  ? 'group-hover:border-gray-400 dark:border-white'
                  : '',
                iconSize
              ]"
            >
              <span
                class="text-gray-500 dark:text-white"
                :class="[step.action ? 'group-hover:text-gray-900' : '']"
              >
                {{ step.id }}
              </span>
            </span>
            <span
              class="capitalize-first ml-4 font-medium text-gray-500 dark:text-white"
              :class="{
                'group-hover:text-gray-900': step.action,
                'text-sm': size === 'extra-small'
              }"
            >
              {{ step.name }}
            </span>
          </span>
        </div>
        <template v-if="stepIdx !== steps.length - 1">
          <!-- Arrow separator for lg screens and up -->
          <div
            class="absolute top-0 right-0 hidden h-full w-5 md:block"
            aria-hidden="true"
          >
            <svg
              v-if="
                steps[stepIdx].status === 'complete' &&
                steps[stepIdx + 1].status !== 'complete'
              "
              class="text-primary h-full w-full bg-white dark:bg-slate-700"
              viewBox="0 0 22 80"
              fill="none"
              preserveAspectRatio="none"
            >
              <polygon
                points="0,0 22,40 0,80"
                vector-effect="non-scaling-stroke"
                stroke="currentcolor"
                fill="currentcolor"
              />
            </svg>
            <svg
              v-else
              class="h-full w-full text-gray-300 dark:text-slate-700"
              viewBox="0 0 22 80"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0 -2L20 40L0 82"
                vector-effect="non-scaling-stroke"
                stroke="currentcolor"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </template>
      </li>
    </ol>
  </nav>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'WizardProgressBar',
  components: { FontAwesomeIcon },
  props: {
    steps: {
      type: Array,
      required: true
    },
    step: {
      type: [Number, String],
      required: true
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
      validator: (value) =>
        ['extra-small', 'small', 'medium', 'large', 'extra-large'].indexOf(
          value
        ) !== -1
    }
  },
  computed: {
    localStep: {
      get() {
        return this.step
      },
      set(value) {
        this.$emit('update:step', value)
      }
    },
    padding() {
      switch (this.size) {
        case 'extra-small':
          return 'px-4 py-2'
        case 'small':
          return 'px-5 py-3'
        case 'medium':
        default:
          return 'px-6 py-4'
      }
    },
    iconSize() {
      switch (this.size) {
        case 'extra-small':
          return 'h-6 w-6'
        case 'small':
          return 'h-8 w-8'
        case 'medium':
        default:
          return 'h-10 w-10'
      }
    }
  }
}
</script>
